import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Listens for fresh Angular Service Worker bundles and prompts the
 * user with a non-blocking "Refresh now" snackbar when one is ready.
 *
 * Why this exists
 * ---------------
 * Angular's Service Worker serves cached bundles aggressively for
 * speed and offline support. Without an explicit update prompt, a
 * user's tab can keep running a stale bundle indefinitely (until
 * every tab is closed and re-opened), and any new API paths shipped
 * server-side will 404 against the cached frontend.
 *
 * This was the literal post-mortem of the 2026-05-22 mobile pricing
 * 404 — the customer-quote-email endpoint had been renamed days
 * earlier, the new bundle was deployed correctly, CloudFront was
 * invalidated, but the test device's SW kept serving the pre-rename
 * code so it kept calling the old (now non-existent) URL.
 *
 * Design constraints
 * ------------------
 *   1. Listen on `versionUpdates$` filtered to `VersionReadyEvent` —
 *      that observable is the durable surface for "a new bundle is
 *      installed and ready to activate". `checkForUpdate()` is only
 *      the initial kick + the periodic re-check that produces those
 *      events; it is NOT what we react to directly.
 *   2. Re-check hourly so long-lived tabs eventually notice a fresh
 *      bundle even if the SW's own polling didn't.
 *   3. Every SW call is guarded by `swUpdate.isEnabled`, so this is
 *      a silent no-op under `ng serve` (dev mode), in browsers
 *      without SW support, and any environment where the SW failed
 *      to register.
 *   4. NEVER auto-reload — that would nuke in-flight form state and
 *      cost the user typed work. The reload only fires after the
 *      user taps the snackbar action.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
    private static readonly RECHECK_INTERVAL_MS = 60 * 60 * 1000;
    private static readonly TOAST_MESSAGE = 'A new version is available.';
    private static readonly TOAST_ACTION = 'Refresh now';

    private readonly _swUpdate = inject(SwUpdate);
    private readonly _snackBar = inject(MatSnackBar);
    private readonly _subscriptions = new Subscription();

    /**
     * Wires the version listener and the periodic re-check. Call
     * once at app bootstrap (see `AppComponent.ngOnInit`). Safe to
     * call multiple times — the SW is shared and additional
     * subscriptions just duplicate work without breaking anything,
     * but the canonical call site is the root component.
     */
    init(): void {
        if (!this._swUpdate.isEnabled) {
            return;
        }

        this._subscriptions.add(
            this._swUpdate.versionUpdates
                .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
                .subscribe(() => this._promptForRefresh()),
        );

        this._subscriptions.add(
            interval(AppUpdateService.RECHECK_INTERVAL_MS).subscribe(() => {
                this._swUpdate.checkForUpdate().catch((err) => {
                    console.warn('[AppUpdateService] periodic checkForUpdate failed:', err);
                });
            }),
        );

        this._swUpdate.checkForUpdate().catch((err) => {
            console.warn('[AppUpdateService] initial checkForUpdate failed:', err);
        });
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }

    /**
     * Open a sticky snackbar (no auto-dismiss) with the action button
     * the user must tap to apply the update. Once tapped, activate
     * the new SW and reload — the reload is what actually swaps the
     * running JS, `activateUpdate()` alone is not enough.
     */
    private _promptForRefresh(): void {
        const snackRef = this._snackBar.open(
            AppUpdateService.TOAST_MESSAGE,
            AppUpdateService.TOAST_ACTION,
            { duration: 0 },
        );

        snackRef.onAction().subscribe(() => {
            this._swUpdate
                .activateUpdate()
                .finally(() => document.location.reload());
        });
    }
}
